/**
 * RowGrid - A grid component for arranging children in a row.
 *
 * @author Koh Tong Seng <tongsengkoh@ifca.com.my>
 * @since 2024-02-05
 *
 * @param {Object} props - The component props
 * @param {React.ReactNode} [props.children] - The children to render
 * @param {string} [props.className] - The CSS class name to apply
 * @param {AlignItemsEnum} [props.crossAxisAlignment] - The cross axis alignment
 * @param {'row'} props.flexDirection - The flexbox direction
 * @param {boolean} [props.fullWidth] - Whether to take up the full width
 * @param {string} [props.gap] - The gap between children
 * @param {string} [props.height] - The height of the component
 * @param {JustifyContentEnum} [props.mainAxisAlignment] - The main axis alignment
 * @param {OverflowEnum} [props.overflow] - The overflow strategy
 *
 * @returns {JSX.Element} The rendered component
 */

import React from 'react'
import { GridX } from '../GridX'
import {
  AlignItemsEnum,
  JustifyContentEnum,
  OverflowEnum,
} from '../LayoutXenum'

type RowGridProps = {
  children?: React.ReactNode
  className?: string
  crossAxisAlignment?: AlignItemsEnum
  fullWidth?: boolean
  gap?: string
  gridTemplateColumns: string
  height?: string
  mainAxisAlignment?: JustifyContentEnum
  overflow?: OverflowEnum
}

export const RowGrid = React.memo<RowGridProps>(
  ({
    children,
    className,
    crossAxisAlignment,
    fullWidth,
    gap,
    gridTemplateColumns,
    height,
    mainAxisAlignment,
    overflow,
  }: RowGridProps) => {
    return (
      <GridX
        children={children}
        className={className}
        crossAxisAlignment={crossAxisAlignment}
        direction="row"
        fullWidth={fullWidth}
        gap={gap}
        gridTemplate={gridTemplateColumns}
        height={height}
        mainAxisAlignment={mainAxisAlignment}
        overflow={overflow}
      />
    )
  }
)
